const IntentType = {
  all: 'all',
  login_code: 'login_code',
  clock_project: 'clock_project',
  clock_employee: 'clock_employee',
} as const

type TIntentType = keyof typeof IntentType

abstract class IntentURL {
  readonly url: URL
  protected static ALLOWED_SERVER_DOMAINS = [
    'clockin.de',
    'd20.clockin.dev',
    'd21.clockin.dev',
    'd22.clockin.dev',
    'd23.clockin.dev',
    'd24.clockin.dev',
    'd25.clockin.dev',
    'd26.clockin.dev',
    'd27.clockin.dev',
    'd28.clockin.dev',
    'd29.clockin.dev',
    'clockin.test',
  ]
  protected static PATH = '/'

  // allow to access static class property PATH also on the instance via this.PATH
  protected get PATH(): string {
    return (this.constructor as typeof IntentURL).PATH
  }

  constructor(url: string | URL) {
    this.url = new URL(url instanceof URL ? url.href : url)
    this.validateOrigin()
    this.validatePath()
  }

  protected validateOrigin(): void {
    const allowedOrigins = IntentURL.ALLOWED_SERVER_DOMAINS.map(
      (domain) => `https://app.${domain}`,
    )

    if (!allowedOrigins.includes(this.url.origin)) {
      throw new InvalidIntentURLError('Invalid origin')
    }
  }

  protected validatePath(): void {
    if (!this.url.pathname.startsWith(this.PATH)) {
      throw new InvalidIntentURLError('Invalid path')
    }
  }

  public static parse(url: string | URL, types: TIntentType = IntentType.all) {
    const classesByCase = {
      [IntentType.login_code]: [UserLoginIntentURL, DeviceLoginIntentURL],
      [IntentType.clock_employee]: [ClockEmployeeIntentURL],
      [IntentType.clock_project]: [ClockProjectIntentURL],
      [IntentType.all]: [
        UserLoginIntentURL,
        DeviceLoginIntentURL,
        ClockEmployeeIntentURL,
        ClockProjectIntentURL,
      ],
    }

    for (const IntentURLSubType of classesByCase[types]) {
      try {
        return new IntentURLSubType(url)
      } catch (e) {
        if (!(e instanceof InvalidIntentURLError)) throw e
      }
    }

    throw new InvalidIntentURLError('Unable to parse url')
  }
}

class CodeIntentURL extends IntentURL {
  protected static CODE_CHARACTERS = 6

  constructor(url: URL | string) {
    super(url)
    this.validateCode()
  }

  public static create(domain: string, code: string) {
    return new this(`https://app.${domain}${this.PATH}${code}`)
  }

  get code() {
    return this.url.pathname.substring(this.PATH.length)
  }

  protected validateCode(): void {
    if (
      this.code.length !==
      (this.constructor as typeof CodeIntentURL).CODE_CHARACTERS
    ) {
      throw new InvalidIntentURLError('Invalid code length')
    }
  }
}

class UserLoginIntentURL extends CodeIntentURL {
  protected static PATH = '/auth/user/login/'
  protected static CODE_CHARACTERS = 6
}

class DeviceLoginIntentURL extends CodeIntentURL {
  protected static PATH = '/auth/device/login/'
  protected static CODE_CHARACTERS = 8
}

class ClockProjectIntentURL extends CodeIntentURL {
  protected static PATH = '/clock/projects/'
  protected static CODE_CHARACTERS = 8
}

class ClockEmployeeIntentURL extends CodeIntentURL {
  protected static PATH = '/clock/employees/'
  protected static CODE_CHARACTERS = 8
}

class InvalidIntentURLError extends Error {}

export {
  IntentURL,
  UserLoginIntentURL,
  DeviceLoginIntentURL,
  InvalidIntentURLError,
  IntentType,
  type TIntentType,
}
